import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

/**
 * Handles legacy Thinkific URL patterns and redirects to new paths.
 * Mounted on /courses/* in the router.
 * 
 * Patterns:
 *   /courses/take/{slug}/...  → /kurse/{matched-slug} or /kurse
 *   /courses/{slug}           → /kurse/{matched-slug} or /kurse
 */
export default function LegacyRedirects() {
  const location = useLocation();
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolve = async () => {
      const path = location.pathname;

      // Extract the Thinkific slug from the URL
      let slug: string | null = null;

      // Pattern: /courses/take/{slug}/... 
      const takeMatch = path.match(/^\/courses\/take\/([^/]+)/);
      if (takeMatch) {
        slug = takeMatch[1];
      } else {
        // Pattern: /courses/{slug}
        const courseMatch = path.match(/^\/courses\/([^/]+)/);
        if (courseMatch) {
          slug = courseMatch[1];
        }
      }

      if (!slug) {
        setRedirectTo("/kurse");
        setLoading(false);
        return;
      }

      // Try to find a matching course in the database
      try {
        // First: try exact slug match
        const { data: bySlug } = await supabase
          .from("courses")
          .select("slug")
          .eq("slug", slug)
          .eq("is_published", true)
          .maybeSingle();

        if (bySlug) {
          setRedirectTo(`/kurse/${bySlug.slug}`);
          setLoading(false);
          return;
        }

        // Second: try matching via thinkific_course_id (some imports stored the Thinkific slug there)
        // Also try a fuzzy slug match (slug contains the thinkific slug or vice versa)
        const { data: allCourses } = await supabase
          .from("courses")
          .select("slug, thinkific_course_id")
          .eq("is_published", true);

        if (allCourses) {
          const normalizedSlug = slug.toLowerCase().replace(/[^a-z0-9]/g, "");

          const match = allCourses.find((c) => {
            const normalizedCourseSlug = c.slug.toLowerCase().replace(/[^a-z0-9]/g, "");
            return (
              normalizedCourseSlug.includes(normalizedSlug) ||
              normalizedSlug.includes(normalizedCourseSlug)
            );
          });

          if (match) {
            setRedirectTo(`/kurse/${match.slug}`);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn("[LegacyRedirects] DB lookup failed:", err);
      }

      // Fallback: redirect to course overview
      setRedirectTo("/kurse");
      setLoading(false);
    };

    resolve();
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <Navigate to={redirectTo || "/kurse"} replace />;
}
